import _extends from "@babel/runtime/helpers/extends";
import React, { useRef } from 'react';
import ReactDOM from 'react-dom';
import { StyledModal, StyledOffsetModal, StyledLightboxModal, StyledLightboxHeader, StyledLightboxCancel, StyledLightboxContent, StyledLightboxTitle, StyledModalDrawer, ModalContentDrawer, ModalBg, CancelButton, CancelButtonWrapper, CancelText, ModalContent, OffsetModalContent, FlexWrapper } from './Modal.styles';
import { CloseSmall, CloseMedium } from '@blueprint/icons';
import { useModal, useFocus } from './Modal.hooks';

function ModalBody(props) {
  var modalRef = props.modalRef,
      close = props.close,
      children = props.children,
      closeTitle = props.closeTitle,
      cancelButton = props.cancelButton,
      modalTitle = props.modalTitle;
  return React.createElement(StyledModal, {
    tabIndex: -1,
    "aria-modal": true,
    role: "dialog",
    "data-testid": "modal",
    ref: modalRef,
    modalTitle: modalTitle,
    className: props.className
  }, cancelButton && !modalTitle && React.createElement(CancelButtonWrapper, null, React.createElement(CancelButton, {
    "aria-label": "Close",
    "data-testid": "modal-close",
    onClick: close,
    title: closeTitle
  }, React.createElement(CloseMedium, null))), cancelButton && modalTitle && React.createElement(StyledLightboxHeader, {
    "data-testid": "modal-title"
  }, React.createElement(StyledLightboxTitle, null, modalTitle), React.createElement(StyledLightboxCancel, {
    "aria-label": "Close",
    "data-testid": "modal-close",
    onClick: close,
    title: "close"
  }, React.createElement(CloseSmall, null))), React.createElement(ModalContent, null, cancelButton && modalTitle ? React.createElement(FlexWrapper, null, children) : children));
}

function ModalDrawerBody(props) {
  var modalRef = props.modalRef,
      close = props.close,
      children = props.children,
      closeTitle = props.closeTitle,
      cancelButton = props.cancelButton,
      modalTitle = props.modalTitle;
  return React.createElement(StyledModalDrawer, {
    tabIndex: -1,
    "aria-modal": true,
    role: "dialog",
    "data-testid": "modal",
    ref: modalRef
  }, cancelButton && !modalTitle && React.createElement(CancelButtonWrapper, null, React.createElement(CancelButton, {
    "aria-label": "Close",
    "data-testid": "modal-close",
    onClick: close,
    title: closeTitle
  }, React.createElement(CloseMedium, null))), cancelButton && modalTitle && React.createElement(StyledLightboxHeader, {
    "data-testid": "modal-title"
  }, React.createElement(StyledLightboxTitle, null, modalTitle), React.createElement(StyledLightboxCancel, {
    "aria-label": "Close",
    "data-testid": "modal-close",
    onClick: close,
    title: closeTitle
  }, React.createElement(CloseSmall, null))), React.createElement(ModalContentDrawer, null, children));
}

function ModalLightboxBody(props) {
  var modalRef = props.modalRef,
      children = props.children,
      close = props.close,
      title = props.title,
      _props$cancelText = props.cancelText,
      cancelText = _props$cancelText === void 0 ? 'Close' : _props$cancelText,
      _props$isFullScreen = props.isFullScreen,
      isFullScreen = _props$isFullScreen === void 0 ? false : _props$isFullScreen;
  return React.createElement(StyledLightboxModal, {
    isFullScreen: isFullScreen,
    tabIndex: -1,
    "aria-modal": true,
    role: "dialog",
    "data-testid": "modal",
    ref: modalRef
  }, React.createElement(StyledLightboxHeader, null, React.createElement(StyledLightboxTitle, null, title), React.createElement(StyledLightboxCancel, {
    "aria-label": "Close",
    "data-testid": "modal-close",
    onClick: close,
    title: "close"
  }, React.createElement(CloseSmall, null), React.createElement(CancelText, null, cancelText))), React.createElement(StyledLightboxContent, null, children));
}

function ModalOffsetBody(props) {
  var modalRef = props.modalRef,
      paddingOverride = props.paddingOverride,
      paddingOverrideLarge = props.paddingOverrideLarge,
      contentHeight = props.contentHeight,
      children = props.children;
  return React.createElement(StyledOffsetModal, {
    tabIndex: -1,
    "aria-modal": true,
    role: "dialog",
    "data-testid": "modal",
    ref: modalRef
  }, React.createElement(OffsetModalContent, {
    paddingOverride: paddingOverride,
    paddingOverrideLarge: paddingOverrideLarge,
    contentHeight: contentHeight
  }, children));
}

function ModalContainer(props) {
  var isShowing = props.isShowing,
      close = props.close,
      modal = props.modal,
      modalRef = props.modalRef;

  var onBgMouseDown = function onBgMouseDown(event) {
    var _modalRef$current;

    if (event.target instanceof Node && !((_modalRef$current = modalRef.current) === null || _modalRef$current === void 0 ? void 0 : _modalRef$current.contains(event.target))) {
      close();
    }
  };

  useFocus(isShowing, modalRef);
  return isShowing ? React.createElement(ModalBg, {
    "data-testid": "modal-bg",
    "data-modal-open": true,
    onMouseDown: onBgMouseDown
  }, modal) : null;
}

function ModalPortal(props) {
  var modalType = props.modalType,
      modalRef = props.modalRef;
  return typeof window !== 'undefined' ? ReactDOM.createPortal(React.createElement(ModalContainer, _extends({}, props, {
    modal: modalType,
    modalRef: modalRef
  })), document.body) : null;
}

function Modal(props) {
  var modalRef = useRef(null);
  var close = props.close,
      _props$closeTitle = props.closeTitle,
      closeTitle = _props$closeTitle === void 0 ? 'close' : _props$closeTitle,
      modalTitle = props.modalTitle,
      _props$cancelButton = props.cancelButton,
      cancelButton = _props$cancelButton === void 0 ? true : _props$cancelButton,
      children = props.children;
  return React.createElement(ModalPortal, _extends({}, props, {
    modalRef: modalRef,
    modalType: React.createElement(ModalBody, {
      cancelButton: cancelButton,
      close: close,
      modalRef: modalRef,
      closeTitle: closeTitle,
      modalTitle: modalTitle,
      className: props.className
    }, children)
  }));
}

function ModalDrawer(props) {
  var modalRef = useRef(null);
  var close = props.close,
      _props$closeTitle2 = props.closeTitle,
      closeTitle = _props$closeTitle2 === void 0 ? 'close' : _props$closeTitle2,
      _props$cancelButton2 = props.cancelButton,
      cancelButton = _props$cancelButton2 === void 0 ? true : _props$cancelButton2,
      children = props.children,
      modalTitle = props.modalTitle;
  return React.createElement(ModalPortal, _extends({}, props, {
    modalRef: modalRef,
    modalType: React.createElement(ModalDrawerBody, {
      cancelButton: cancelButton,
      close: close,
      modalRef: modalRef,
      closeTitle: closeTitle,
      modalTitle: modalTitle
    }, children)
  }));
}

function ModalLightbox(props) {
  var modalRef = useRef(null);
  var close = props.close,
      _props$title = props.title,
      title = _props$title === void 0 ? 'title' : _props$title,
      children = props.children,
      isFullScreen = props.isFullScreen;
  return React.createElement(ModalPortal, _extends({}, props, {
    modalRef: modalRef,
    modalType: React.createElement(ModalLightboxBody, {
      isFullScreen: isFullScreen,
      close: close,
      modalRef: modalRef,
      title: title
    }, children)
  }));
}

function ModalOffset(props) {
  var modalRef = useRef(null);
  var close = props.close,
      paddingOverride = props.paddingOverride,
      paddingOverrideLarge = props.paddingOverrideLarge,
      contentHeight = props.contentHeight,
      children = props.children;
  return React.createElement(ModalPortal, _extends({}, props, {
    modalRef: modalRef,
    modalType: React.createElement(ModalOffsetBody, {
      close: close,
      paddingOverride: paddingOverride,
      paddingOverrideLarge: paddingOverrideLarge,
      contentHeight: contentHeight,
      modalRef: modalRef
    }, children)
  }));
}

export { Modal, useModal, ModalLightbox, ModalOffset, ModalDrawer };
//# sourceMappingURL=index.js.map